<template>
  <v-container fluid>
    <v-card flat class="mb-4">
      <v-card-title>
        <v-icon left>mdi-clipboard-text-clock</v-icon>
        Audit Log
      </v-card-title>

      <v-card-text>
        <v-row dense>
          <v-col cols="12" sm="3">
            <v-select
              v-model="filter.action"
              :items="actionOptions"
              label="Action"
              clearable
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-select
              v-model="filter.module"
              :items="moduleOptions"
              label="Module"
              clearable
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="filter.username"
              label="Username"
              clearable
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="filter.dateStart"
              label="Date Start"
              type="date"
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" sm="3">
            <v-text-field
              v-model="filter.dateEnd"
              label="Date End"
              type="date"
              dense
              outlined
            />
          </v-col>
          <v-col cols="12" sm="3" class="d-flex align-center">
            <v-btn color="primary" @click="doSearch" :loading="loading">
              <v-icon left>mdi-magnify</v-icon>
              Search
            </v-btn>
            <v-btn class="ml-2" text @click="resetFilter">
              Reset
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card flat>
      <v-card-title>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Filter in results..."
          single-line
          hide-details
          dense
          outlined
          style="max-width: 300px"
        />
        <v-spacer />
        <span class="caption grey--text">{{ logs.length }} records</span>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="logs"
        :search="search"
        :loading="loading"
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
        sort-by="createdAt"
        sort-desc
        dense
      >
        <template v-slot:[`item.action`]="{ item }">
          <v-chip
            small
            :color="actionColor(item.action)"
            dark
            label
          >
            {{ item.action }}
          </v-chip>
        </template>

        <template v-slot:[`item.module`]="{ item }">
          <span class="text-capitalize">{{ item.module }}</span>
        </template>

        <template v-slot:[`item.createdAt`]="{ item }">
          {{ formatDate(item.createdAt) }}
        </template>

        <template v-slot:[`item.data`]="{ item }">
          <v-btn icon x-small @click="showDetail(item)">
            <v-icon small>mdi-eye</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Detail Dialog -->
    <v-dialog v-model="detailDialog" max-width="600">
      <v-card v-if="selectedLog">
        <v-card-title>
          <v-chip small :color="actionColor(selectedLog.action)" dark label class="mr-2">
            {{ selectedLog.action }}
          </v-chip>
          <span class="text-capitalize">{{ selectedLog.module }}</span>
        </v-card-title>
        <v-card-text>
          <v-simple-table dense>
            <tbody>
              <tr>
                <td class="font-weight-medium" style="width:140px">Time</td>
                <td>{{ formatDate(selectedLog.createdAt) }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">Username</td>
                <td>{{ selectedLog.username }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">Module</td>
                <td class="text-capitalize">{{ selectedLog.module }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">Action</td>
                <td>{{ selectedLog.action }}</td>
              </tr>
              <tr>
                <td class="font-weight-medium">Target ID</td>
                <td>{{ selectedLog.targetId || '-' }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <div class="mt-3" v-if="selectedLog.data && Object.keys(selectedLog.data).length">
            <div class="caption font-weight-medium mb-1">Data</div>
            <v-sheet outlined rounded class="pa-3">
              <pre class="caption" style="white-space: pre-wrap; word-break: break-all;">{{ JSON.stringify(selectedLog.data, null, 2) }}</pre>
            </v-sheet>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="detailDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "AuditLogs",
  data() {
    return {
      search: "",
      loading: false,
      detailDialog: false,
      selectedLog: null,
      filter: {
        action: null,
        module: null,
        username: "",
        dateStart: "",
        dateEnd: "",
      },
      actionOptions: ["CREATE", "UPDATE", "DELETE"],
      moduleOptions: [
        "users",
        "stations",
        "sectors",
        "offices",
        "trucks",
        "types",
        "articles",
        "categories",
      ],
      headers: [
        { text: "Time", value: "createdAt", width: "180px" },
        { text: "Username", value: "username", width: "140px" },
        { text: "Module", value: "module", width: "120px" },
        { text: "Action", value: "action", width: "110px" },
        { text: "Target ID", value: "targetId" },
        { text: "Detail", value: "data", sortable: false, width: "80px" },
      ],
    };
  },
  computed: {
    ...mapGetters("auditlogs", ["getAuditLogs"]),
    logs() {
      return this.getAuditLogs;
    },
  },
  mounted() {
    this.fetchLogs();
  },
  methods: {
    async fetchLogs() {
      this.loading = true;
      await this.$store.dispatch("auditlogs/fetchAll");
      this.loading = false;
    },
    async doSearch() {
      this.loading = true;
      const f = { ...this.filter };
      // Remove empty values
      Object.keys(f).forEach((k) => {
        if (!f[k]) delete f[k];
      });
      await this.$store.dispatch("auditlogs/search", f);
      this.loading = false;
    },
    resetFilter() {
      this.filter = { action: null, module: null, username: "", dateStart: "", dateEnd: "" };
      this.fetchLogs();
    },
    showDetail(item) {
      this.selectedLog = item;
      this.detailDialog = true;
    },
    actionColor(action) {
      if (action === "CREATE") return "green";
      if (action === "DELETE") return "red";
      return "orange";
    },
    formatDate(val) {
      if (!val) return "-";
      const d = new Date(val);
      return d.toLocaleString("th-TH", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
  },
};
</script>
